import { memo, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { HandleModalProps } from "@/types/props";
import { emptyBachelorDegree } from "@/models/empty";
import { iBachelorDegree } from "@/types/models";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ZodError } from "../ZodDisplayError";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { CalendarIcon, ChevronDown, RefreshCw } from "lucide-react";
import { Calendar } from "../ui/calendar";
import { bachelorDegreeSchema, tBachelorDegreeSchema } from "../validations/bachelorDegree";
import { classificationList, ethnicCodeAndNameList, majorCodesWithName, nationalityCodeAndNameList, requestTypeList, trainingMethodList } from "../validations/shared";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { cn } from "../ui/utils";
import { NgocInput } from "../ui/ngocInput";
import { getLocalDate } from "@/lib/utils/func";

export const BachelorDegreeModal = memo(({ isOpen, onClose, onAdd, onUpdate, editData, mode }: HandleModalProps) => {
	const {
		control,
		register,
		handleSubmit,
		reset,
		setValue,
		getValues,
		trigger,
		formState: { errors, isValid },
	} = useForm<tBachelorDegreeSchema>({
		resolver: zodResolver(bachelorDegreeSchema),
		defaultValues: emptyBachelorDegree,
		mode: "all",
	});

	const submit = (data: tBachelorDegreeSchema) => {
		switch (mode) {
			case "create":
				onAdd(data as Omit<iBachelorDegree, "id">);
				break;
			case "edit":
				onUpdate(data);
				break;
		}
		onClose();
	};

	useEffect(() => {
		switch (mode) {
			case "create":
				reset(emptyBachelorDegree);
				break;
			case "edit":

				reset({
					...editData,
					currentDegreeNumber: editData.degreeNumber || "",
					currentStudentId: editData.studentId || "",
					currentCCCD: editData.cccd || "",
					currentMajorCode: editData.majorCode || "",
					dateOfBirth: editData.dateOfBirth ? getLocalDate(editData.dateOfBirth) : emptyBachelorDegree.dateOfBirth,
					signDate: editData.signDate ? getLocalDate(editData.signDate) : emptyBachelorDegree.signDate,
					ethnicCode: editData.ethnicCode || "",
					ethnicity: editData.ethnicity || "",
					nationalityCode: editData.nationalityCode || "",
					nationality: editData.nationality || "",
					classificationId: editData.classificationId || "",
					signerTitle: editData.signerTitle || "",
					recognitionDecisionNumber: editData.recognitionDecisionNumber || "",
					requestType: editData.requestType || "",
					notes: editData.notes || "",
					classification: editData.classification.toString() || "",
					graduationYear: editData.graduationYear.toString() || "",
					requestTypeId: editData.requestTypeId.toString() || "",
				});
				trigger();
				break;
		}
	}, [isOpen, editData, mode, reset, trigger]);

	const logError = (e: any) => {
		console.log(e);
	};

	const [openDateOfBirthSelected, setOpenDateOfBirthSelected] = useState(false);
	const [searchMajorName, setSearchMajorName] = useState("");
	const [openSignDateSelected, setOpenSignDateSelected] = useState(false);

	const years = useMemo(() => {
		const list = [];
		for (let y = 1995; y <= new Date().getFullYear(); y++) list.unshift(y);
		return list;
	}, []);

	return (
		<Dialog
			open={isOpen}
			onOpenChange={() => {
				onClose();
				setSearchMajorName("");
			}}
		>
			<DialogContent className='max-w-6xl'>
				<DialogHeader>
					<DialogTitle>{mode === "create" ? "Thêm Mới" : "Cập Nhật Bằng Đại Học"}</DialogTitle>
				</DialogHeader>
				<form onSubmit={handleSubmit(submit, logError)} id='handleBachelorDegreeForm' className='grid grid-cols-2 gap-2 px-1 max-h-[75dvh] overflow-y-auto'>
					<div className='col-span-2'>
						<h3 className='font-semibold text-lg text-red-600'>Thông tin cá nhân</h3>
					</div>
					<div className='space-y-1'>
						<Label className='required'>Họ và tên</Label>
						<NgocInput placeholder='Nhập họ tên' maxLength={250} {...register("studentName")} />

						<ZodError message={errors.studentName?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Mã sinh viên </Label>
						<NgocInput placeholder='Nhập Mã sinh viên' maxLength={250} {...register("studentId")} />

						<ZodError message={errors.studentId?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Số CCCD </Label>
						<NgocInput placeholder='Nhập số CCCD' maxLength={12} {...register("cccd")} />

						<ZodError message={errors.cccd?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Ngày sinh </Label>
						<Controller
							control={control}
							name='dateOfBirth'
							render={({ field }) => (
								<Popover open={openDateOfBirthSelected} onOpenChange={(value) => { setOpenDateOfBirthSelected(value); if (!value) trigger("dateOfBirth"); }}>
									<PopoverTrigger asChild>
										<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
											<CalendarIcon className='mr-2 h-4 w-4' />
											{field.value === undefined ? "Chọn ngày sinh" : format(field.value, "dd/MM/yyyy", { locale: vi })}
										</Button>
									</PopoverTrigger>
									<PopoverContent className='w-auto p-0'>
										<Calendar
											mode='single'
											selected={field.value as unknown as Date}
											defaultMonth={field.value === undefined ? field.value : new Date(field.value)}
											onSelect={(value: any) => {
												if (value) {
													field.onChange(getLocalDate(value));
													setOpenDateOfBirthSelected(false);
												}
											}}
											initialFocus
										/>
									</PopoverContent>
								</Popover>
							)}
						/>

						<ZodError message={errors.dateOfBirth?.message} />
					</div>
					<div className='space-y-1'>
						<Label className=''>Nơi sinh </Label>
						<NgocInput placeholder='Nhập nơi sinh' maxLength={250} {...register("placeOfBirth")} />

						<ZodError message={errors.placeOfBirth?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Giới tính </Label>
						<Controller
							control={control}
							name='gender'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn giới tính' />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='Nam'>Nam</SelectItem>
										<SelectItem value='Nữ'>Nữ</SelectItem>
									</SelectContent>
								</Select>
							)}
						/>

						<ZodError message={errors.gender?.message} />
					</div>
					<div className='space-y-1'>
						<Label>Dân tộc</Label>
						<Controller
							control={control}
							name='ethnicCode'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
										const targetEthnic = ethnicCodeAndNameList.find((x) => x.code == value);
										setValue("ethnicity", targetEthnic!.name);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn dân tộc' />
									</SelectTrigger>
									<SelectContent className='h-64 overflow-y-scroll'>
										{ethnicCodeAndNameList.map((et) => {
											return (
												<SelectItem key={et.code} value={et.code}>
													({et.code}) {et.name}
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.ethnicCode?.message} />
					</div>
					<div className='space-y-1'>
						<Label>Quốc tịch</Label>
						<Controller
							control={control}
							name='nationalityCode'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
										const targetNation = nationalityCodeAndNameList.find((x) => x.code == value);
										setValue("nationality", targetNation!.name);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn quốc tịch' />
									</SelectTrigger>
									<SelectContent className='h-64 overflow-y-scroll'>
										{nationalityCodeAndNameList.map((et) => {
											return (
												<SelectItem key={et.code} value={et.code}>
													({et.code}) {et.name}
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.nationalityCode?.message} />
					</div>

					{/* <div className='space-y-1'>
						<Label>Mã Dân tộc</Label>
						<Input placeholder='Nhập mã dân tộc' {...register("ethnicCode")} autoComplete='off' />

						<ZodError message={errors.ethnicCode?.message} />
					</div>
					<div className='space-y-1'>
						<Label>Dân tộc</Label>
						<Input placeholder='Nhập tên dân tộc' {...register("ethnicity")} autoComplete='off' />

						<ZodError message={errors.ethnicity?.message} />
					</div> */}
					{/* <div className='space-y-1'>
						<Label>Mã quốc tịch</Label>
						<Input placeholder='Nhập mã quốc tịch' {...register("nationalityCode")} autoComplete='off' />

						<ZodError message={errors.nationalityCode?.message} />
					</div>
					<div className='space-y-1'>
						<Label>Quốc tịch</Label>
						<Input placeholder='Nhập quốc tịch' {...register("nationality")} autoComplete='off' />

						<ZodError message={errors.nationality?.message} />
					</div> */}

					{/* Thông tin văn bằng */}
					<div className='col-span-2 mt-4'>
						<h3 className='font-semibold text-lg text-red-600'>Thông tin văn bằng</h3>
					</div>
					<div className='space-y-1'>
						<Label className='required'>Ngành học</Label>
						{/* <Input placeholder='Nhập mã ngành' {...register("majorCode")} autoComplete='off' /> */}
						<Controller
							control={control}
							name='majorCode'
							render={({ field }) => (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button variant='outline' className={cn("w-full justify-between text-slate-700 font-normal")}>
											{getValues("majorCode") == "" ? "Chọn ngành" : `(${getValues("majorCode")}) ${getValues("major")}`}
											<ChevronDown />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent>
										<div className='flex gap-1'>
											<input
												value={searchMajorName}
												onInput={(e: any) => {
													setSearchMajorName(e.target.value);
												}}
												type='text'
												className='border w-full px-1'
												placeholder='Tìm kiếm...'
												onKeyDown={(e) => {
													e.stopPropagation();
												}}
											/>
											<button
												onClick={() => {
													setSearchMajorName("");
												}}
												type='button'
												className='cursor-pointer text-sm'
											>
												<RefreshCw size={"1rem"} />
											</button>
										</div>
										<div className='h-40 overflow-y-scroll'>
											{majorCodesWithName
												.filter((mj) => mj.name.toLocaleLowerCase().includes(searchMajorName.trim().toLowerCase()))
												.map((mj) => {
													return (
														<DropdownMenuCheckboxItem
															key={mj.code}
															checked={getValues("majorCode") === mj.code}
															onCheckedChange={() => {
																field.onChange(mj.code);
																const targetMajor = majorCodesWithName.find((smj) => smj.code === mj.code);
																setValue("major", targetMajor!.name);
																trigger("cccd");
															}}
														>
															({mj.code}) {mj.name}
														</DropdownMenuCheckboxItem>
													);
												})}
										</div>
									</DropdownMenuContent>
								</DropdownMenu>
							)}
						/>

						{/* <Select
							value={field.value}
							onValueChange={(value) => {
								field.onChange(value);
								const targetMajor = majorCodesWithName.find((mj) => mj.code === value);
								setValue("major", targetMajor!.name);
							}}
						>
							<SelectTrigger>
								<SelectValue placeholder='Chọn mã ngành' />
							</SelectTrigger>
							<SelectContent>
								
							</SelectContent>
						</Select> */}
						<ZodError message={errors.majorCode?.message} />
					</div>
					{/* <div className='space-y-1'>
						<Label className='required'>Ngành</Label>
						<Controller
							control={control}
							name='major'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
									disabled
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn ngành đào tạo' />
									</SelectTrigger>
									<SelectContent>
										{majorCodesWithName.map((mj) => {
											return (
												<SelectItem key={mj.code + mj.name} value={majorCodesWithName[mj.code]}>
													({mj.code}) {mj.name}
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>
							)}
						/>

						<ZodError message={errors.major?.message} />
					</div> */}
					<div className='space-y-1'>
						<Label className='required'>Năm tốt nghiệp</Label>
						<Controller
							control={control}
							name='graduationYear'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn năm' />
									</SelectTrigger>
									<SelectContent className='h-fit max-h-60 overflow-y-scroll'>
										{years.map((y) => {
											return (
												<SelectItem key={y} value={y.toString()}>
													{y}
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>
							)}
						/>

						<ZodError message={errors.graduationYear?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Xếp loại tốt nghiệp</Label>
						<Controller
							control={control}
							name='classificationId'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
										const selectedClassification = classificationList.find((x) => x.code == value);
										setValue("classification", selectedClassification!.name);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn xếp loại' />
									</SelectTrigger>
									<SelectContent>
										{classificationList.map((cl) => {
											return (
												<SelectItem key={cl.code} value={cl.code}>
													({cl.code}) {cl.name}
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>
							)}
						/>

						<ZodError message={errors.classification?.message} />
					</div>
					{/* <div className='space-y-1'>
						<Label className='required'>Xếp loại tốt nghiệp</Label>
						<Controller
							control={control}
							name='classification'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn ID xếp loại' />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='Xuất sắc'>Xuất sắc</SelectItem>
										<SelectItem value='Giỏi'>Giỏi</SelectItem>
										<SelectItem value='Khá'>Khá</SelectItem>
										<SelectItem value='Trung bình'>Trung bình</SelectItem>
									</SelectContent>
								</Select>
							)}
						/>

						<ZodError message={errors.classificationId?.message} />
					</div> */}
					<div className='space-y-1'>
						<Label className='required'>Hình thức đào tạo</Label>
						<Controller
							control={control}
							name='trainingMode'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn hình thức' />
									</SelectTrigger>
									<SelectContent>
										{trainingMethodList.map((me) => {
											return (
												<SelectItem key={me.code} value={me.code}>
													{me.name}
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>
							)}
						/>

						<ZodError message={errors.trainingMode?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Số hiệu bằng (cơ sở)</Label>
						<NgocInput
							placeholder='Nhập số liệu văn bằng'
							maxLength={250}
							{...register("degreeNumber")}
						/>

						<ZodError message={errors.degreeNumber?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Số vào sổ cấp bằng</Label>
						<NgocInput placeholder='Nhập số vào sổ câp văn bằng' maxLength={50} {...register("registrationNumber")} />

						<ZodError message={errors.registrationNumber?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Người ký</Label>
						<NgocInput placeholder='Nhập tên người ký' maxLength={250} {...register("signedBy")} />

						<ZodError message={errors.signedBy?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Chức danh người ký</Label>
						<NgocInput placeholder='Nhập chức danh người ký' maxLength={250} {...register("signerTitle")} />

						<ZodError message={errors.signerTitle?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Ngày ký </Label>
						<Controller
							control={control}
							name='signDate'
							render={({ field }) => (
								<Popover open={openSignDateSelected} onOpenChange={(value) => { setOpenSignDateSelected(value); if (!value) trigger("signDate"); }}>
									<PopoverTrigger asChild>
										<Button variant='outline' className='w-full justify-start text-left font-normal bg-transparent'>
											<CalendarIcon className='mr-2 h-4 w-4' />
											{field.value === undefined ? "Chọn ngày ký" : format(field.value, "dd/MM/yyyy", { locale: vi })}
										</Button>
									</PopoverTrigger>
									<PopoverContent className='w-auto p-0'>
										<Calendar
											mode='single'
											selected={field.value as unknown as Date}
											defaultMonth={field.value === undefined ? field.value : new Date(field.value)}
											onSelect={(value: any) => {
												if (value) {
													field.onChange(getLocalDate(value));
													setOpenSignDateSelected(false);
												}
											}}
											initialFocus
										/>
									</PopoverContent>
								</Popover>
							)}
						/>
						<ZodError message={errors.signDate?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Khoá học</Label>
						<NgocInput placeholder='Nhập tên khóa học' maxLength={250} {...register("course")} />

						<ZodError message={errors.course?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Quyết định công nhận số</Label>
						<NgocInput placeholder='Nhập quyết định' maxLength={250} {...register("recognitionDecisionNumber")} />

						<ZodError message={errors.recognitionDecisionNumber?.message} />
					</div>
					<div className='space-y-1'>
						<Label className='required'>Loại yêu cầu</Label>
						<Controller
							control={control}
							name='requestTypeId'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
										const targetRequestType = requestTypeList.find((x) => x.code == value);
										setValue("requestType", targetRequestType!.name);
									}}
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn loại yêu cầu' />
									</SelectTrigger>
									<SelectContent>
										{requestTypeList.map((rq) => {
											return (
												<SelectItem key={rq.code} value={rq.code}>
													({rq.code}) {rq.name}
												</SelectItem>
											);
										})}
									</SelectContent>
								</Select>
							)}
						/>

						<ZodError message={errors.requestTypeId?.message} />
					</div>
					{/* <div className='space-y-1'>
						<Label>Loại yêu cầu</Label>
						<Controller
							control={control}
							name='requestType'
							render={({ field }) => (
								<Select
									value={field.value}
									onValueChange={(value) => {
										field.onChange(value);
									}}
									disabled
								>
									<SelectTrigger>
										<SelectValue placeholder='Chọn loại yêu cầu' />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value='Cấp mới'>Cấp mới</SelectItem>
										<SelectItem value='Chỉnh sửa'>Chỉnh sửa</SelectItem>
										<SelectItem value='Huỷ bằng'>Huỷ bằng</SelectItem>
									</SelectContent>
								</Select>
							)}
						/>
						<ZodError message={errors.requestType?.message} />
					</div> */}
					<div className='space-y-1'>
						<Label>Trình độ</Label>
						<Input value={"Đại học"} readOnly disabled />
					</div>
					<div className='col-span-2 space-y-1'>
						<Label>Ghi chú</Label>
						<NgocInput rows={3} placeholder='Nhập ghi chú' maxLength={500} {...register("notes")} />

						<ZodError message={errors.notes?.message} />
					</div>
				</form>
				<div className='flex justify-end gap-2'>
					<Button
						variant='destructive'
						onClick={() => {
							onClose();
							setSearchMajorName("");
						}}
					>
						Hủy
					</Button>
					<Button disabled={!isValid} variant={"add"} type='submit' form='handleBachelorDegreeForm'>
						Lưu
					</Button>
				</div>
			</DialogContent>
		</Dialog >
	);
});
